import { BaseService } from '@common/http-layer/service/base.service'

import type {
  EventsAttendeesByContactResponse,
  EventsManagementAttendeesParams,
  EventsManagementAttendeesResponse,
  EventsManagementDetailResponse,
  EventsManagementExhibitorMembersParams,
  EventsManagementExhibitorMembersResponse,
  EventsManagementExhibitorsParams,
  EventsManagementExhibitorsResponse,
  EventsManagementSavedSessionParams,
  EventsManagementScheduleParams,
  EventsManagementScheduleResponse,
  EventsManagementSpeakersResponse,
  EventsPortalListParams,
  EventsPortalListResponse,
  EventsPortalStatePage,
  EventsPortalState,
  EventsTicketsParams,
} from '../types/events-management'

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const DEFAULT_PAGE_LIMIT = 20

const eventState = (value: unknown, now: number): EventsPortalState => {
  const event = asRecord(value)
  const start = typeof event?.startDateTime === 'string' ? Date.parse(event.startDateTime) : Number.NaN
  const end = typeof event?.endDateTime === 'string' ? Date.parse(event.endDateTime) : Number.NaN

  if (Number.isFinite(end) && end < now) return 'past'
  if (Number.isFinite(start) && start <= now && (!Number.isFinite(end) || end >= now)) return 'live'
  return 'upcoming'
}

const parsePortalStatePage = (
  events: readonly unknown[],
  state: EventsPortalState,
  requestedState: EventsPortalState | undefined,
  pageInfo: Record<string, unknown> | undefined,
  limit: number,
  now: number
): EventsPortalStatePage => {
  const stateEvents = events.filter(event => eventState(event, now) === state)
  const carriesCursor = requestedState === undefined || requestedState === state
  const nextCursor =
    carriesCursor && typeof pageInfo?.nextCursor === 'string' && pageInfo.nextCursor.trim() ? pageInfo.nextCursor.trim() : undefined
  const hasMore = carriesCursor && pageInfo?.hasNext === true && Boolean(nextCursor)

  return {
    events: stateEvents,
    pagination: {
      limit,
      total: stateEvents.length,
      hasMore,
      ...(hasMore && nextCursor ? { nextCursor } : {}),
    },
  }
}

export class EventsService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '/clientportal-middleware/events-management',
      headers: { developer_version: 'nuxt-events-cp-access' },
    })
  }

  async getPortalEvents(params: EventsPortalListParams): Promise<EventsPortalListResponse> {
    const limit = params.limit ?? DEFAULT_PAGE_LIMIT

    const { data } = await this.get<unknown>('/internal/portal/events', {
      params: {
        ...(params.cursor?.trim() ? { cursor: params.cursor.trim() } : {}),
        limit,
      },
    })

    const payload = asRecord(data)
    const events = payload && Array.isArray(payload.events) ? payload.events : []
    const pageInfo = asRecord(payload?.pageInfo)
    const now = Date.now()

    return {
      live: parsePortalStatePage(events, 'live', params.state, pageInfo, limit, now),
      upcoming: parsePortalStatePage(events, 'upcoming', params.state, pageInfo, limit, now),
      past: parsePortalStatePage(events, 'past', params.state, pageInfo, limit, now),
    }
  }

  async getEventTickets(params: EventsTicketsParams): Promise<EventsAttendeesByContactResponse> {
    const eventId = params.eventId.trim()
    if (!eventId) {
      throw new Error('Event ID is required for fetching tickets')
    }

    const limit = params.limit ?? DEFAULT_PAGE_LIMIT
    const { data } = await this.get<unknown>(`/internal/portal/events/${encodeURIComponent(eventId)}/tickets`, {
      params: {
        ...(params.cursor?.trim() ? { cursor: params.cursor.trim() } : {}),
        limit,
      },
    })

    const payload = asRecord(data)
    const attendees = payload && Array.isArray(payload.tickets) ? payload.tickets : []
    const pageInfo = asRecord(payload?.pageInfo)
    const nextCursor = typeof pageInfo?.nextCursor === 'string' && pageInfo.nextCursor.trim() ? pageInfo.nextCursor.trim() : undefined
    const hasMore = pageInfo?.hasNext === true && Boolean(nextCursor)

    return {
      attendees,
      meta: {
        contacts: {},
        tickets: {},
        orders: {},
      },
      pagination: {
        total: attendees.length,
        limit,
        hasMore,
        ...(hasMore && nextCursor ? { nextCursor } : {}),
      },
    }
  }

  async getPublicEvent(eventId: string): Promise<EventsManagementDetailResponse> {
    const resolvedEventId = eventId.trim()
    if (!resolvedEventId) {
      throw new Error('Event ID is required for fetching event detail')
    }

    const { data } = await this.get<unknown>(`/public/events/${encodeURIComponent(resolvedEventId)}`)
    const payload = asRecord(data)
    const event = payload?.event
    return { event: event === undefined ? null : event }
  }

  async getPublicEventSchedule(params: EventsManagementScheduleParams): Promise<EventsManagementScheduleResponse> {
    const resolvedEventId = params.eventId.trim()
    const contactId = params.contactId.trim()
    if (!resolvedEventId) {
      throw new Error('Event ID is required for fetching event schedule')
    }
    if (!contactId) {
      throw new Error('Contact ID is required for fetching event schedule')
    }

    // TODO: Remove the contactId query param once events-management resolves the viewer from auth/JWT server-side.
    const { data } = await this.get<unknown>(`/public/events/${encodeURIComponent(resolvedEventId)}/schedule`, {
      params: {
        contactId,
        ...(params.speakerId?.trim() ? { speakerId: params.speakerId.trim() } : {}),
      },
    })
    const payload = asRecord(data)
    const sessions = payload && Array.isArray(payload.sessions) ? payload.sessions : []
    const settings = asRecord(payload?.settings)
    const timezone = typeof settings?.timezone === 'string' && settings.timezone.trim() ? settings.timezone.trim() : undefined
    return timezone ? { sessions, timezone } : { sessions }
  }

  async getPublicEventSpeakers(eventId: string): Promise<EventsManagementSpeakersResponse> {
    const resolvedEventId = eventId.trim()
    if (!resolvedEventId) {
      throw new Error('Event ID is required for fetching event speakers')
    }

    const { data } = await this.get<unknown>(`/public/events/${encodeURIComponent(resolvedEventId)}/speakers`)
    const payload = asRecord(data)
    const speakers = payload && Array.isArray(payload.speakers) ? payload.speakers : []
    return { speakers }
  }

  async getPublicEventAttendees(eventId: string, params: EventsManagementAttendeesParams = {}): Promise<EventsManagementAttendeesResponse> {
    const resolvedEventId = eventId.trim()
    if (!resolvedEventId) {
      throw new Error('Event ID is required for fetching event attendees')
    }

    const page = params.page ?? 1
    const pageLimit = params.pageLimit ?? 20
    const { data } = await this.get<unknown>(`/public/events/${encodeURIComponent(resolvedEventId)}/attendees`, {
      headers: { developer_version: 'add-attendee' },
      params: { page, pageLimit },
    })
    const payload = asRecord(data)
    const attendees = payload && Array.isArray(payload.attendees) ? payload.attendees : []
    const paginationPayload = asRecord(payload?.pagination)
    const total = typeof paginationPayload?.total === 'number' ? paginationPayload.total : attendees.length
    const resolvedPage = typeof paginationPayload?.page === 'number' ? paginationPayload.page : page
    const resolvedPageLimit = typeof paginationPayload?.pageLimit === 'number' ? paginationPayload.pageLimit : pageLimit
    const hasMore = paginationPayload?.hasMore === true
    return {
      attendees,
      pagination: { total, page: resolvedPage, pageLimit: resolvedPageLimit, hasMore },
    }
  }

  async updatePublicEventSavedSession(params: EventsManagementSavedSessionParams): Promise<void> {
    const eventId = params.eventId.trim()
    const sessionId = params.sessionId.trim()
    const contactId = params.contactId.trim()
    if (!eventId) {
      throw new Error('Event ID is required for saving a schedule session')
    }
    if (!sessionId) {
      throw new Error('Session ID is required for saving a schedule session')
    }
    if (!contactId) {
      throw new Error('Contact ID is required for saving a schedule session')
    }

    await this.put(`/public/events/${encodeURIComponent(eventId)}/schedule/saved-sessions/${encodeURIComponent(sessionId)}`, {
      saved: params.saved,
      contactId,
    })
  }

  async listExhibitors(params: EventsManagementExhibitorsParams): Promise<EventsManagementExhibitorsResponse> {
    const eventId = params.eventId.trim()
    const locationId = params.locationId.trim()
    if (!eventId) {
      throw new Error('Event ID is required for fetching exhibitors')
    }
    if (!locationId) {
      throw new Error('Location ID is required for fetching exhibitors')
    }

    const skip = params.skip ?? 0
    const limit = params.limit ?? 20
    const { data } = await this.post<unknown>('/exhibitors/list', { eventId, locationId, skip, limit })
    const payload = asRecord(data)
    const exhibitors = payload && Array.isArray(payload.exhibitors) ? payload.exhibitors : []
    const paginationPayload = asRecord(payload?.pagination)
    const metaPayload = asRecord(payload?.meta)
    const tiers = metaPayload && Array.isArray(metaPayload.tiers) ? metaPayload.tiers : []
    return {
      exhibitors,
      meta: { tiers },
      pagination: {
        skip: typeof paginationPayload?.skip === 'number' ? paginationPayload.skip : skip,
        limit: typeof paginationPayload?.limit === 'number' ? paginationPayload.limit : limit,
        total: typeof paginationPayload?.total === 'number' ? paginationPayload.total : exhibitors.length,
      },
    }
  }

  async listExhibitorMembers(params: EventsManagementExhibitorMembersParams): Promise<EventsManagementExhibitorMembersResponse> {
    const eventId = params.eventId.trim()
    const exhibitorId = params.exhibitorId.trim()
    const locationId = params.locationId.trim()
    if (!eventId) {
      throw new Error('Event ID is required for fetching exhibitor members')
    }
    if (!exhibitorId) {
      throw new Error('Exhibitor ID is required for fetching exhibitor members')
    }
    if (!locationId) {
      throw new Error('Location ID is required for fetching exhibitor members')
    }

    const { data } = await this.get<unknown>(`/exhibitors/${encodeURIComponent(eventId)}/${encodeURIComponent(exhibitorId)}/members`, {
      params: { locationId },
    })
    const payload = asRecord(data)
    const members = payload && Array.isArray(payload.members) ? payload.members : []
    return { members }
  }
}
